import { z } from 'zod'
import type { Permission } from './permissions.js'

/**
 * Files and attachments (ADR-016, design D10–D12). A file is stored once and bound to the records it
 * belongs to; who may see or change it follows from those records (the ATTACHABLE registry).
 */

/**
 * Record types that take attachments through `/files` and `/attachments`. `sales_invoice` covers
 * invoices and debit notes (one table). Posted and void documents take attachments too: an
 * attachment never changes the accounting.
 */
export const ATTACHMENT_ENTITY_TYPES = [
  'customer',
  'supplier',
  'product',
  'sales_invoice',
  'quotation',
  'credit_note',
  'receipt',
  'customer_refund',
  'purchase',
  'supplier_payment',
  'supplier_return',
  'supplier_refund',
  'expense',
  'journal_entry',
  'bank_statement',
  'task',
  'service_job',
  'service_visit',
  'service_agreement',
  'service_collection',
  /** C1: an asset's photos and documents (nameplate, warranty card). */
  'service_asset',
  /** C1: a site's files, e.g. the asset import's CSV (the BOQ pattern). */
  'service_site',
  /** C2: a maintenance contract's files (the signed copy). */
  'service_contract',
  /** C3: photos of one asset's check on a visit (the check's id). */
  'service_visit_check',
  'project',
  'project_material_issue',
  'project_cost_entry',
  'project_variation',
  'project_claim',
  'project_guarantee',
  /** ADR-048: an employee's file (scanned civil ID, passport, contract, certificates). */
  'employee',
] as const
export type AttachmentEntityType = (typeof ATTACHMENT_ENTITY_TYPES)[number]

/**
 * Everything a file can be bound to: the attachment types plus the company logo (`tenant_branding`,
 * managed only through `/settings/company/logo`).
 */
export type AttachableEntityType = AttachmentEntityType | 'tenant_branding'

export interface AttachableAccess {
  /** Needed to list and download the record's files; null = any signed-in user. */
  read: Permission | null
  /** Any one of these adds and removes the record's files. */
  write: readonly [Permission, ...Permission[]]
}

/**
 * Who may see and change a record's files: its module's read permission, and the permission that
 * creates or edits the record. The web shows the attachments panel and its buttons by the same rule.
 */
export const ATTACHABLE_ACCESS: Record<AttachableEntityType, AttachableAccess> = {
  customer: { read: 'customers.read', write: ['customers.update', 'customers.create'] },
  supplier: { read: 'suppliers.read', write: ['suppliers.update', 'suppliers.create'] },
  product: { read: 'products.read', write: ['products.update', 'products.create'] },
  sales_invoice: { read: 'sales.read', write: ['invoices.create'] },
  quotation: { read: 'sales.read', write: ['quotations.create'] },
  credit_note: { read: 'sales.read', write: ['creditNotes.create'] },
  receipt: { read: 'sales.read', write: ['receipts.create'] },
  customer_refund: { read: 'sales.read', write: ['refunds.create'] },
  purchase: { read: 'purchasing.read', write: ['purchases.create'] },
  supplier_payment: { read: 'purchasing.read', write: ['supplierPayments.create'] },
  supplier_return: { read: 'purchasing.read', write: ['supplierReturns.create'] },
  supplier_refund: { read: 'purchasing.read', write: ['supplierRefunds.create'] },
  expense: { read: 'expenses.read', write: ['expenses.update', 'expenses.create'] },
  journal_entry: { read: 'ledger.read', write: ['journals.post'] },
  bank_statement: { read: 'treasury.read', write: ['treasury.reconcile'] },
  // Tasks add a record-level check on top (visibility; who may work on the task): see the API's
  // ATTACHABLE registry.
  task: { read: 'tasks.read', write: ['tasks.read'] },
  // Service files: a visit's only by its team (or a manager), on top of the permission (API registry).
  service_job: { read: 'service.read', write: ['service.create', 'service.work', 'service.manage'] },
  service_visit: { read: 'service.read', write: ['service.work', 'service.manage'] },
  service_agreement: { read: 'service.read', write: ['service.create', 'service.work', 'service.manage'] },
  service_collection: { read: 'service.read', write: ['service.collect', 'service.verify_payments'] },
  service_asset: { read: 'service.read', write: ['service.assets'] },
  service_site: { read: 'service.read', write: ['service.assets'] },
  service_contract: { read: 'contracts.read', write: ['contracts.manage'] },
  // Written by the visit's team while the visit is open (the API's record check, as for service_visit).
  service_visit_check: { read: 'service.read', write: ['service.work', 'service.manage'] },
  // Project files: the record-level check (a member, or projects.manage; or projects.contract, which
  // attaches the BOQ CSV it imports; the project within the data scope) is in the API's ATTACHABLE registry.
  project: { read: 'projects.read', write: ['projects.read', 'projects.contract'] },
  // Material issues and cost entries (projects/02 §5–6): read with projects.materials, projects.cost_entries
  // or projects.financials as their scope reaches the project(s); the API's registry checks the record.
  project_material_issue: { read: null, write: ['projects.materials'] },
  project_cost_entry: { read: null, write: ['projects.cost_entries'] },
  // Contracting (projects/04 §6): read with projects.contract or projects.financials reaching the project;
  // written with projects.contract, or by who records the client's side (the decision on a variation, the
  // certificate of a claim); the API's registry checks the record.
  project_variation: { read: null, write: ['projects.contract', 'projects.variations_approve'] },
  project_claim: { read: null, write: ['projects.contract', 'projects.claims_certify'] },
  // Guarantees (P5): read through the record's project scope (projects.contract or projects.financials).
  project_guarantee: { read: null, write: ['projects.contract'] },
  employee: { read: 'employees.read', write: ['employees.documents', 'employees.manage'] },
  tenant_branding: { read: null, write: ['settings.manage'] },
}

/**
 * The accepted file types (ASM-066). The type comes from the content (magic bytes), never from the
 * name or the browser's declared type; the server sets the stored content type. Office files are
 * recognised as OOXML packages and must carry the matching extension; CSV and text must be UTF-8
 * without NUL bytes and are never shown inline. No SVG, HTML, XML or executables.
 */
export const FILE_TYPES = {
  pdf: { contentType: 'application/pdf', extensions: ['pdf'], inline: true },
  png: { contentType: 'image/png', extensions: ['png'], inline: true },
  jpeg: { contentType: 'image/jpeg', extensions: ['jpg', 'jpeg'], inline: true },
  webp: { contentType: 'image/webp', extensions: ['webp'], inline: true },
  docx: {
    contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extensions: ['docx'],
    inline: false,
  },
  xlsx: {
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extensions: ['xlsx'],
    inline: false,
  },
  csv: { contentType: 'text/csv', extensions: ['csv'], inline: false },
  txt: { contentType: 'text/plain', extensions: ['txt'], inline: false },
} as const satisfies Record<string, { contentType: string; extensions: readonly string[]; inline: boolean }>
export type FileType = keyof typeof FILE_TYPES
export type FileContentType = (typeof FILE_TYPES)[FileType]['contentType']

/** Types an attachment may have. */
export const ATTACHMENT_FILE_TYPES: readonly FileType[] = [
  'pdf',
  'png',
  'jpeg',
  'webp',
  'docx',
  'xlsx',
  'csv',
  'txt',
]
/** The company logo: raster images only. */
export const LOGO_FILE_TYPES: readonly FileType[] = ['png', 'jpeg', 'webp']
/** The logo's size limit (bytes) and largest width or height (pixels). */
export const LOGO_MAX_BYTES = 1024 * 1024
export const LOGO_MAX_DIMENSION = 2000

/**
 * The company's images (`tenant_branding` bindings, one per purpose): the logo, the authorised
 * signature and the company stamp. All three follow the logo's rules (types, size, dimensions).
 */
export const BRANDING_PURPOSES = ['logo', 'signature', 'stamp'] as const
export type BrandingPurpose = (typeof BRANDING_PURPOSES)[number]

/** For `<input type="file" accept>`: the extensions the server can accept. */
export const ATTACHMENT_ACCEPT = ATTACHMENT_FILE_TYPES.flatMap((t) =>
  FILE_TYPES[t].extensions.map((e) => `.${e}`),
).join(',')
export const LOGO_ACCEPT = LOGO_FILE_TYPES.flatMap((t) => FILE_TYPES[t].extensions.map((e) => `.${e}`)).join(
  ',',
)

/**
 * Upload refusals, besides the usual 403/404. The size limit of attachments is the server's
 * FILES_MAX_BYTES (10 MiB unless configured).
 */
export const FileErrorCode = {
  /** 413: over the size limit. Nothing is kept. */
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  /** 422: the content is not one of the accepted types (or does not match its Office extension). */
  UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',
  /** 422: no multipart `file` field, more than one file, or an empty file. */
  FILE_REQUIRED: 'FILE_REQUIRED',
  /** 422: a logo wider or taller than LOGO_MAX_DIMENSION pixels. */
  IMAGE_TOO_LARGE: 'IMAGE_TOO_LARGE',
  /** 422: the file is held for a virus scan (or failed it) and cannot be downloaded. */
  FILE_UNAVAILABLE: 'FILE_UNAVAILABLE',
} as const

/** A stored file, as lists and uploads return it. Never its storage location. */
export interface FileInfo {
  id: string
  /** The sanitised original name; its extension always matches the stored type. */
  name: string
  contentType: FileContentType
  /** Bytes. */
  size: number
  uploadedAt: string
  /** Null for files the system or the legacy migration stored. */
  uploadedBy: { id: string; name: string } | null
}

const entityTypeSchema = z.enum(ATTACHMENT_ENTITY_TYPES)

/**
 * `POST /files?entityType&entityId` (multipart field `file`) and `GET /attachments?entityType&entityId`.
 * A malformed or unknown `entityId` is a 404, like any record not visible in the tenant.
 */
export const attachmentTargetSchema = z.object({
  entityType: entityTypeSchema,
  entityId: z.string().max(100),
})
export type AttachmentTarget = z.input<typeof attachmentTargetSchema>

/** `DELETE /attachments?entityType&entityId&fileId`: unbinds one file from one record. */
export const attachmentRemoveSchema = attachmentTargetSchema.extend({ fileId: z.string().max(100) })
export type AttachmentRemove = z.input<typeof attachmentRemoveSchema>

/** At most this many ids per counts request (one list page). */
export const ATTACHMENT_COUNTS_MAX_IDS = 200

/** `GET /attachments/counts?entityType&ids=a,b,c`: how many files each record has (the paperclip). */
export const attachmentCountsQuerySchema = z.object({
  entityType: entityTypeSchema,
  ids: z
    .string()
    .transform((v) => [
      ...new Set(
        v
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    ])
    .pipe(z.array(z.uuid()).min(1).max(ATTACHMENT_COUNTS_MAX_IDS)),
})
export type AttachmentCountsQuery = z.input<typeof attachmentCountsQuerySchema>

/** Every requested id, with 0 for records without files. */
export interface AttachmentCounts {
  counts: Record<string, number>
}
